import { Map } from 'immutable';
import Logger from 'js-logger';
import moment from 'moment-timezone';
import { createReducer } from 'redux-act';

import { defaultHoursCreated } from '@/store/actions/setting/createDefaultHours';
import { getEntityFromMap } from '@/utils/entityUtils';

import Contact from '../../models/interfaces/Contact';
import FacebookPage from '../../models/interfaces/FacebookPage';
import Hours from '../../models/interfaces/Hours';
import PlaceLocalization from '../../models/interfaces/PlaceLocalization';
import PlaceProfile from '../../models/interfaces/PlaceProfile';
import ReservationSetting from '../../models/interfaces/ReservationSetting';
import { authenticationRequired } from '../actions/authentication/authenticationRequired';
import { logout } from '../actions/authentication/logout';
import { specialHourAdded } from '../actions/setting/addSpecialHour';
import { specialHourDeleted } from '../actions/setting/deleteSpecialHour';
import { facebookPageFetched, facebookPageFetchFailed } from '../actions/setting/facebook/fetchFacebookPage';
import { facebookPagesFetched, facebookPagesFetchFailed } from '../actions/setting/facebook/fetchFacebookPages';
import { facebookPageSaved } from '../actions/setting/facebook/saveFacebookPage';
import * as fetchContact from '../actions/setting/fetchContact';
import { hoursFetched } from '../actions/setting/fetchHours';
import { placeLocalizationFetched, PlaceLocalizationFetchedPayload } from '../actions/setting/fetchPlaceLocalization';
import * as fetchProfile from '../actions/setting/fetchProfile';
import * as fetchReservationSetting from '../actions/setting/fetchReservationSetting';
import { specialHourModified } from '../actions/setting/modifySpecialHour';
import * as saveContact from '../actions/setting/saveContact';
import { regularHoursSaved } from '../actions/setting/saveHours';
import * as saveProfile from '../actions/setting/saveProfile';
import * as saveReservationSetting from '../actions/setting/saveReservationSetting';

export interface SettingState {
  readonly placeContacts: Map<string, Contact>;
  readonly placeHours: Map<string, Hours>;
  readonly placeReservationSettings: Map<string, ReservationSetting>;
  readonly placeProfiles: Map<string, PlaceProfile>;
  readonly placeLocalizations: Map<string, PlaceLocalization>;
  readonly facebookPages: Map<string, FacebookPage[]>;
  readonly facebookPage: Map<string, FacebookPage | null>;
}

const defaultState: SettingState = {
  placeContacts: Map<string, Contact>(),
  placeHours: Map<string, Hours>(),
  placeReservationSettings: Map<string, ReservationSetting>(),
  placeProfiles: Map<string, PlaceProfile>(),
  placeLocalizations: Map<string, PlaceLocalization>(),
  facebookPages: Map<string, FacebookPage[]>(),
  facebookPage: Map<string, FacebookPage | null>(),
};

const settingsReducer = createReducer<SettingState>({}, defaultState);

settingsReducer.on(fetchContact.contactFetched, (settings, { contact }) => ({
  ...settings,
  placeContacts: settings.placeContacts.set(contact.placeUuid, contact),
}));

settingsReducer.on(facebookPagesFetched, (settings, { facebookPages, placeUuid }) => ({
  ...settings,
  facebookPages: settings.facebookPages.set(placeUuid, facebookPages),
}));

settingsReducer.on(facebookPagesFetchFailed, (settings, { placeUuid }) => ({
  ...settings,
  facebookPages: settings.facebookPages.set(placeUuid, []),
}));

settingsReducer.on(facebookPageFetched, (settings, { facebookPage, placeUuid }) => ({
  ...settings,
  facebookPage: settings.facebookPage.set(placeUuid, facebookPage),
}));

settingsReducer.on(facebookPageFetchFailed, (settings, { placeUuid }) => ({
  ...settings,
  facebookPage: settings.facebookPage.remove(placeUuid),
}));

settingsReducer.on(fetchProfile.profileFetched, (settings, { profile }) => ({
  ...settings,
  placeProfiles: settings.placeProfiles.set(profile.placeUuid, profile),
}));

settingsReducer.on(hoursFetched, (settings, { hours }) => ({
  ...settings,
  placeHours: settings.placeHours.set(hours.placeUuid, hours),
}));

settingsReducer.on(defaultHoursCreated, (settings, { hours }) => ({
  ...settings,
  placeHours: settings.placeHours.set(hours.placeUuid, hours),
}));

settingsReducer.on(fetchReservationSetting.reservationSettingFetched, (settings, { reservationSetting }) => ({
  ...settings,
  placeReservationSettings: settings.placeReservationSettings.set(reservationSetting.placeUuid, reservationSetting),
}));

settingsReducer.on(placeLocalizationFetched, (settings, { placeLocalization }: PlaceLocalizationFetchedPayload) => {
  try {
    // TODO: move side effect outside of reducer
    moment.tz.setDefault(placeLocalization.timezone);
  } catch (err) {
    Logger.error(`Invalid timezone. ${err}`);
  }
  return {
    ...settings,
    placeLocalizations: settings.placeLocalizations.set(placeLocalization.placeUuid, placeLocalization),
  };
});

settingsReducer.on(facebookPageSaved, (settings, { placeUuid, facebookPage }) => ({
  ...settings,
  facebookPage: settings.facebookPage.set(placeUuid, facebookPage),
}));

settingsReducer.on(saveContact.contactSaved, (settings, { contact }) => ({
  ...settings,
  placeContacts: settings.placeContacts.set(contact.placeUuid, contact),
}));

settingsReducer.on(regularHoursSaved, (settings, { regularHours, placeUuid }): SettingState => ({
  ...settings,
  placeHours: settings.placeHours.set(placeUuid, {
    ...getEntityFromMap(settings.placeHours, placeUuid),
    regularHours: Map(regularHours.map((regularHour) => [regularHour.dayIndex, regularHour])),
  }),
}));

settingsReducer.on(specialHourAdded, (settings, { specialHour, placeUuid }) => {
  const placeHours = getEntityFromMap(settings.placeHours, placeUuid);
  return {
    ...settings,
    placeHours: settings.placeHours.set(placeUuid, {
      ...placeHours,
      specialHours: [...placeHours.specialHours, specialHour],
    }),
  };
});

settingsReducer.on(specialHourModified, (settings, { specialHour: modified, placeUuid }) => {
  const placeHours = getEntityFromMap(settings.placeHours, placeUuid);
  return {
    ...settings,
    placeHours: settings.placeHours.set(placeUuid, {
      ...placeHours,
      specialHours: placeHours.specialHours.map((specialHour) => (specialHour.uuid === modified.uuid ? modified : specialHour)),
    }),
  };
});

settingsReducer.on(specialHourDeleted, (settings, { specialHour, placeUuid }) => {
  const placeHours = getEntityFromMap(settings.placeHours, placeUuid);
  return {
    ...settings,
    placeHours: settings.placeHours.set(placeUuid, {
      ...placeHours,
      specialHours: placeHours.specialHours.filter(({ uuid }) => uuid !== specialHour.uuid),
    }),
  };
});

settingsReducer.on(saveReservationSetting.reservationSettingSaved, (settings, { reservationSetting }) => ({
  ...settings,
  placeReservationSettings: settings.placeReservationSettings.set(reservationSetting.placeUuid, reservationSetting),
}));

settingsReducer.on(saveProfile.profileSaved, (settings, { profile }) => ({
  ...settings,
  placeProfiles: settings.placeProfiles.set(profile.placeUuid, profile),
}));

settingsReducer.on(logout, () => defaultState);

settingsReducer.on(authenticationRequired, () => defaultState);

export default settingsReducer;
